import { Injectable } from '@angular/core';
import { MsalGuardConfiguration, MsalInterceptorConfiguration, MsalService } from '@azure/msal-angular';
import {
	AccountInfo,
	BrowserCacheLocation,
	InteractionRequiredAuthError,
	InteractionType,
	IPublicClientApplication,
	LogLevel,
	PublicClientApplication
} from '@azure/msal-browser';
import { BehaviorSubject } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AppConfig } from '../../config/app-config';
import { decodeJwtPayload } from '../../utils/jwt.util';
import { tap } from 'rxjs/operators';
@Injectable()
export class AuthService {
	currentAccount$: BehaviorSubject<AccountInfo | null> = new BehaviorSubject<AccountInfo | null>(null);
	constructor(private msalService: MsalService) {}
	isLoggedIn(): boolean {
		const accounts: AccountInfo[] = this.msalService.instance.getAllAccounts();
		return accounts.length > 0;
	}
	logout(): void {
		this.msalService
			.logoutRedirect()
			.pipe(
				tap(() => {
					this.currentAccount$.next(null);
				})
			)
			.subscribe();
	}
	initCurrentUser(): void {
		if (this.isLoggedIn() && this.currentAccount$.getValue() === null) {
			const account = this.msalService.instance.getAllAccounts()[0];
			this.currentAccount$.next(account);
			this.msalService.instance.setActiveAccount(account);
		}
	}
	/**
	 * Acquires an access token for the given scopes and returns its DECODED PAYLOAD (not just
	 * the raw string). Use this instead of `account.idTokenClaims` whenever you need claims that
	 * only appear in the ACCESS token (e.g. `roles`, `groups`, `scp`) - the ID token and access
	 * token do NOT necessarily carry the same claims.
	 *
	 * Deliberately SILENT-ONLY: never falls back to an interactive redirect on failure (e.g.
	 * `InteractionRequiredAuthError` when admin consent is missing for the requested scopes).
	 * Letting `MsalInterceptor`/`acquireTokenRedirect` trigger an interactive consent prompt for
	 * a resource whose consent hasn't been granted yet can hijack the whole app's navigation -
	 * this method just returns `null` and logs a warning instead, so a missing-consent resource
	 * (e.g. Microsoft Graph before `Group.Read.All` is granted) degrades gracefully.
	 */
	async getAccessTokenClaims<T = Record<string, unknown>>(scopes: string[]): Promise<T | null> {
		const account = this.currentAccount$.getValue() ?? this.msalService.instance.getActiveAccount();
		if (!account) {
			return null;
		}
		try {
			const result = await this.msalService.instance.acquireTokenSilent({ scopes, account });
			return decodeJwtPayload<T>(result.accessToken);
		} catch (err) {
			if (err instanceof InteractionRequiredAuthError) {
				console.warn(
					'[AuthService] acquireTokenSilent requires interaction for scopes',
					scopes,
					'- likely missing admin consent on the app registration. Not triggering an interactive redirect automatically. Claims for this resource will be unavailable.'
				);
			} else {
				console.warn('[AuthService] acquireTokenSilent failed for scopes', scopes, err);
			}
			return null;
		}
	}
}
export function loggerCallback(logLevel: LogLevel, message: string): void {
	if (!environment.production) {
		console.log(`[${logLevel}] ${message}`);
	}
}
export function MSALInstanceFactory(): IPublicClientApplication {
	return new PublicClientApplication({
		auth: {
			clientId: environment.msalConfig.auth.clientId,
			authority: environment.msalConfig.auth.authority,
			redirectUri: environment.msalConfig.auth.redirectUri,
			postLogoutRedirectUri: '/'
		},
		cache: {
			cacheLocation: BrowserCacheLocation.LocalStorage,
			storeAuthStateInCookie: true
		},
		system: {
			// MSAL auth config (clientId/authority/redirectUri/apiScope) is compiled in from environment.ts -
			// on real deploys, environment.ts is OVERWRITTEN at build time from environment.template.ts via
			// GitHub Actions secrets (AZURE_AD_CLIENT_ID / AZURE_AD_TENANT_ID / AZURE_AD_API_SCOPE /
			// REDIRECT_URI, scoped per GitHub Environment), same mechanism as document-service-frontend's
			// cd.yml. See doc/migration/cognito-to-msal/TODO-NEXT.md.
			loggerOptions: {
				loggerCallback,
				// Verbose + PII logging was used temporarily to diagnose the initial "no login page" issue
				// (2026-09-03/04). Login flow confirmed working end-to-end on 2026-09-04 - reverted to
				// standard non-PII logging. Bump back to LogLevel.Verbose + piiLoggingEnabled: true only for
				// ad-hoc debugging, never commit it enabled.
				logLevel: LogLevel.Info,
				piiLoggingEnabled: false
			}
		}
	});
}
// TODO: POC value - the protectedResourceMap uses a static environment.backendUrl. Once the real Azure AD app is
// wired, derive this dynamically from the runtime AppConfig (assets/config/json/config.json) instead - see TODO-NEXT.md.
//
// Module-level singleton Map, built once with only the static apiConfig.uri entry. MsalInterceptor keeps a
// reference to this very Map instance (via MSAL_INTERCEPTOR_CONFIG), so mutating it later (see
// registerBackendUrlAsProtectedResource below) is enough to make new entries visible to subsequent requests -
// there is no need (and it would be unsafe) to rebuild the Map inside the factory itself.
const protectedResourceMap = new Map<string, Array<string>>();
protectedResourceMap.set(environment.apiConfig.uri, environment.apiConfig.scopes);

export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
	return {
		interactionType: InteractionType.Redirect,
		protectedResourceMap
	};
}
// IMPORTANT: MSAL_INTERCEPTOR_CONFIG (and therefore this factory) is resolved by Angular's DI the FIRST time
// MsalInterceptor is constructed - which happens on the very first HttpClient call in the app, i.e. the
// APP_INITIALIZER call that loads config.json/config.dev.json itself (see app-init.ts). At that exact moment,
// AppConfig.instance is still undefined (it's precisely what's being loaded), so this factory must NEVER read
// AppConfig.instance.backendUrl synchronously: doing so used to throw a TypeError during DI resolution, making
// BOTH config file HTTP calls fail immediately (surfacing as an empty `{}` error, since JSON.stringify(TypeError)
// has no enumerable props) -> APP_INITIALIZER rejected -> blank page.
//
// Instead, call this function once AppConfig.load() has resolved (see app-init.ts) to add the backendUrl entry
// to the SAME Map instance already wired into MsalInterceptor, so real /u2m/* calls made after bootstrap are
// correctly recognized as protected resources.
export function registerBackendUrlAsProtectedResource(): void {
	if (AppConfig.instance?.backendUrl && !protectedResourceMap.has(AppConfig.instance.backendUrl + '/u2m/*')) {
		protectedResourceMap.set(AppConfig.instance.backendUrl + '/u2m/*', environment.apiConfig.scopes);
	}
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
	return {
		interactionType: InteractionType.Redirect,
		authRequest: {
			scopes: environment.apiConfig.scopes
		},
		loginFailedRoute: '/'
	};
}
